<script setup lang="ts">
import { computed, ref, useTemplateRef } from 'vue';
import OverlayPanel from 'primevue/overlaypanel';

const props = defineProps<{
  field: any;
  rowData: any;
  settings: any;
}>();

const listPanel = useTemplateRef<InstanceType<typeof OverlayPanel> | null>('listPanel');

const isHovering = ref(false);

const count = computed(() => Number(props.rowData?.[props.settings?.render?.countKey] ?? 0));
const hasItems = computed(() => count.value > 0);
const labels = computed(() => getLabels());
const hiddenCount = computed(() => count.value - labels.value.length);
const tooltipKey = computed(() => props.settings?.render?.tooltipKey ?? 'common.viewFull');

// HELPERS
function getLabels(): string[] {
  const { labelKey, listKey } = props.settings?.render ?? {};
  const list = props.rowData?.[listKey];

  if (!Array.isArray(list)) return [];

  return list.map((item) => (labelKey ? item?.[labelKey] : item) ?? '');
}

// EVENTS
function onCellEnter(event: Event) {
  isHovering.value = true;
  listPanel.value?.show(event);
}

function onCellLeave() {
  isHovering.value = false;

  setTimeout(() => (!isHovering.value ? listPanel.value?.hide() : ''), 100);
}

function onPanelEnter() {
  isHovering.value = true;
}
</script>

<template>
  <div
    v-if="hasItems"
    class="flex items-center justify-between gap-2"
    @click="onCellEnter"
    @mouseenter="onCellEnter"
    @mouseleave="onCellLeave"
  >
    <p>{{ count }}</p>

    <i
      v-tooltip.top="$t(tooltipKey)"
      class="pi pi-eye text-sm text-slate-500"
    ></i>

    <OverlayPanel
      ref="listPanel"
      @mouseenter="onPanelEnter"
      @mouseleave="onCellLeave"
    >
      <ul class="max-h-[450px] max-w-[300px] overflow-auto text-sm">
        <li
          v-for="(label, index) in labels"
          class="border-t py-1 first:border-t-0 first:pt-0 last:pb-0"
          :key="index"
        >
          {{ label || '-' }}
        </li>

        <li
          v-if="hiddenCount > 0"
          class="border-t py-1 text-slate-500 last:pb-0"
        >
          +{{ hiddenCount }} more
        </li>
      </ul>
    </OverlayPanel>
  </div>

  <p v-else>{{ settings?.isNumber ? '0' : '-' }}</p>
</template>
